import axios from 'axios';
import { getAccessToken } from '../../auth';
import { IUser } from '@elyx-code/definitions';
import { Logger } from '@elyx-code/common-ts-utils';
import { envConfig } from '../../config';

const cachedUsersById: { [id: string]: IUser } = {};
const cachedUsersByEmail: { [email: string]: IUser } = {};

// Often a list of users with repeated users is fetched all at once, so we cache the promises
const currentlyFetchingById: {
	[id: string]: Promise<{ data: { data: IUser } }>;
} = {};
const currentlyFetchingByEmail: {
	[email: string]: Promise<{ data: { data: IUser } }>;
} = {};

let userTokenPromise: Promise<string> | null = null;

async function userPromise(where: { id: string } | { email: string }) {
	userTokenPromise = userTokenPromise || getAccessToken();

	const token = await userTokenPromise;

	Logger.warn('Get user where', where);

	// Fetch user from API
	const promise = axios.get(
		`${envConfig.API_BASE_URL}/user-service/v1/user?${
			// @ts-ignore
			where.email ? `email=${encodeURIComponent(where.email)}` : `id=${encodeURIComponent(where.id)}`
		}`,
		{
			headers: {
				Authorization: 'Bearer ' + token,
			},
		}
	);

	return promise;
}

export async function getUser(
	where: { id: string } | { email: string }
): Promise<IUser | null> {
	try {
		// @ts-ignore
		if (where.id && cachedUsersById[where.id]) {
			Logger.log('Accessing cached user by id');
			// @ts-ignore
			return cachedUsersById[where.id];
		}

		// @ts-ignore
		if (where.email && cachedUsersByEmail[where.email]) {
			// @ts-ignore
			return cachedUsersByEmail[where.email];
		}

		// Check if user is already being fetched
		// @ts-ignore
		if (where.id && currentlyFetchingById[where.id]) {
			Logger.log('Accessing user promise by id');

			// @ts-ignore
			const response = await currentlyFetchingById[where.id];

			return response.data?.data;
		}

		// @ts-ignore
		if (where.email && currentlyFetchingByEmail[where.email]) {
			// @ts-ignore
			const response = await currentlyFetchingByEmail[where.email];

			return response.data?.data;
		}

		// Fetch user from API - Everything that takes long goes in there, grouped as a single promise
		const promise = userPromise(where);

		// @ts-ignore
		if (where.id) {
			Logger.log('Setting user promise by id');
			// @ts-ignore
			currentlyFetchingById[where.id] = promise;
		}

		// @ts-ignore
		if (where.email) {
			// @ts-ignore
			currentlyFetchingByEmail[where.email] = promise;
		}

		const response = await promise;

		if (!!response.data?.data) {
			cachedUsersByEmail[response.data.data.email] = response.data.data;
			cachedUsersById[response.data.data.id] = response.data.data;

			// @ts-ignore
			if (where.id) {
				// @ts-ignore
				delete currentlyFetchingById[where.id];
			}

			// @ts-ignore
			if (where.email) {
				// @ts-ignore
				delete currentlyFetchingByEmail[where.email];
			}

			return response.data.data;
		}

		return null;
	} catch (error) {
		Logger.log('Error fetching user', error);

		return null;
	}
}

export function resolveUserDisplayLabel(user: Partial<IUser>, fallback: string = 'Account deleted'): string {
	let label = '';

	if (!user) {
		return fallback;
	}

	if (!user.name && !user.lastName) {
		return user.email;
	}

	if (user.name) {
		label += user.name;
	}

	if (user.lastName) {
		label += ' ' + user.lastName;
	}

	return label;
}
